import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    IconButton,
    Paper,
    TextField,
    InputAdornment,
    Chip,
    Stack,
} from '@mui/material';
import {
    QrCodeScanner as QrCodeIcon,
    Close as CloseIcon,
    CameraAlt as CameraIcon,
    Search as SearchIcon,
    Send as SendIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import jsQR from 'jsqr';

const QRScanner = ({ open, onClose, onScan, existingScannedIds = [] }) => {
    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState('');
    const [manualCode, setManualCode] = useState('');
    const [scannedItems, setScannedItems] = useState([]);
    const [isComplete, setIsComplete] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const audioContextRef = useRef(null);
    const scanTimeoutRef = useRef(null);
    const isProcessingRef = useRef(false);

    // Initialize audio context for tick sound (same as @yudiel/react-qr-scanner)
    useEffect(() => {
        if (typeof window !== 'undefined') {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return () => {
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    // Play the exact same beep sound as @yudiel/react-qr-scanner
    const playTickSound = useCallback(() => {
        try {
            if (audioContextRef.current) {
                const now = audioContextRef.current.currentTime;

                const oscillator = audioContextRef.current.createOscillator();
                const gainNode = audioContextRef.current.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(audioContextRef.current.destination);

                oscillator.type = 'sine';
                oscillator.frequency.value = 800;

                gainNode.gain.setValueAtTime(1, now);
                gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

                oscillator.start(now);
                oscillator.stop(now + 0.1);
            }
        } catch (error) {
            console.warn('Could not play beep sound:', error);
        }
    }, []);

    // Stop camera
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            const tracks = streamRef.current.getTracks();
            tracks.forEach(track => track.stop());
            streamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setScanning(false);
        setIsPaused(false);

        if (scanTimeoutRef.current) {
            clearTimeout(scanTimeoutRef.current);
            scanTimeoutRef.current = null;
        }
    }, []);

    // Start camera
    const startCamera = useCallback(async () => {
        try {
            setError('');
            stopCamera();

            const constraints = {
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                setScanning(true);
                setIsPaused(false);
            }
        } catch (err) {
            console.error('Camera error:', err);
            setError(`Camera error: ${err.message}`);
            setScanning(false);
        }
    }, [stopCamera]);

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setScannedItems([]);
            setError('');
            setIsComplete(false);
            setIsPaused(false);
            setManualCode('');
            isProcessingRef.current = false;
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }
            startCamera();
        } else {
            stopCamera();
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
                scanTimeoutRef.current = null;
            }
        }
    }, [open, startCamera, stopCamera]);

    // Handle QR detection with continuous scanning
    const handleQRDetected = useCallback((qrData) => {
        if (isProcessingRef.current || isComplete) return;

        const scannedQR = qrData.trim();

        const isDuplicate = scannedItems.some(item => item.qr === scannedQR) ||
            existingScannedIds.includes(scannedQR);

        if (isDuplicate) {
            try {
                if (audioContextRef.current) {
                    const now = audioContextRef.current.currentTime;
                    const oscillator = audioContextRef.current.createOscillator();
                    const gainNode = audioContextRef.current.createGain();
                    oscillator.connect(gainNode);
                    gainNode.connect(audioContextRef.current.destination);
                    oscillator.frequency.value = 400;
                    oscillator.type = 'sine';
                    gainNode.gain.setValueAtTime(0.5, now);
                    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
                    oscillator.start(now);
                    oscillator.stop(now + 0.08);
                }
            } catch (e) { }
            return;
        }

        isProcessingRef.current = true;
        playTickSound();

        const newItem = { qr: scannedQR, timestamp: Date.now() };
        setScannedItems(prev => [...prev, newItem]);

        if (onScan) {
            onScan(scannedQR);
        }

        setTimeout(() => {
            isProcessingRef.current = false;
        }, 300);
    }, [scannedItems, existingScannedIds, playTickSound, onScan, isComplete]);

    // Scan QR Code using jsQR (continuous scanning)
    const scanQRCode = useCallback(() => {
        if (!videoRef.current || !canvasRef.current || !scanning || isPaused || isComplete) {
            return;
        }

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        if (video.readyState !== video.HAVE_ENOUGH_DATA) {
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }
            scanTimeoutRef.current = setTimeout(() => {
                scanQRCode();
            }, 500);
            return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const code = jsQR(imageData.data, canvas.width, canvas.height, {
            inversionAttempts: "dontInvert",
        });

        if (code && code.data) {
            handleQRDetected(code.data);
        }

        if (scanning && !isPaused && !isComplete) {
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }
            scanTimeoutRef.current = setTimeout(() => {
                scanQRCode();
            }, 500);
        }
    }, [scanning, isPaused, isComplete, handleQRDetected]);

    // Auto-scan loop
    useEffect(() => {
        if (scanning && !isPaused && !isComplete) {
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }
            scanTimeoutRef.current = setTimeout(() => {
                scanQRCode();
            }, 300);
        }

        return () => {
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
                scanTimeoutRef.current = null;
            }
        };
    }, [scanning, isPaused, isComplete, scanQRCode]);

    // Handle manual QR entry
    const handleManualSubmit = () => {
        if (!manualCode.trim()) {
            setError('Please enter a QR code');
            return;
        }
        handleQRDetected(manualCode);
        setManualCode('');
    };

    // Handle pause/resume scanning
    const togglePause = () => {
        setIsPaused(prev => !prev);
        if (isPaused) {
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }
            scanTimeoutRef.current = setTimeout(() => {
                scanQRCode();
            }, 300);
        }
    };

    // Handle complete scanning
    const handleComplete = () => {
        setIsComplete(true);
        setIsPaused(true);
        stopCamera();
    };

    // Handle close with confirmation if items scanned
    const handleClose = () => {
        if (scannedItems.length > 0 && !isComplete) {
            if (window.confirm(`You have scanned ${scannedItems.length} item(s). Are you sure you want to close?`)) {
                stopCamera();
                onClose();
            }
        } else {
            stopCamera();
            onClose();
        }
    };

    // Handle close after complete
    const handleCompleteClose = () => {
        stopCamera();
        onClose();
    };

    const getScannedCount = () => scannedItems.length;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            <DialogTitle sx={{
                backgroundColor: isComplete ? 'success.main' : 'primary.main',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <QrCodeIcon sx={{ fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isComplete ? 'Scan Complete!' : 'Scan QR Code'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!isComplete && getScannedCount() > 0 && (
                        <Chip
                            label={`${getScannedCount()} scanned`}
                            size="small"
                            color="success"
                            variant="filled"
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                '& .MuiChip-label': { fontWeight: 600 }
                            }}
                        />
                    )}
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ py: 3 }}>
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: 2 }}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {isComplete ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                mx: 'auto',
                                mb: 3,
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 48, color: 'white' }} />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                            Successfully Scanned!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {getScannedCount()} item{getScannedCount() !== 1 ? 's' : ''} added to transaction
                        </Typography>
                        {getScannedCount() > 0 && (
                            <Paper
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    backgroundColor: 'background.default',
                                    borderRadius: 2,
                                    textAlign: 'left',
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                    Scanned QR Codes:
                                </Typography>
                                {scannedItems.map((item, index) => (
                                    <Box
                                        key={item.timestamp}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            py: 0.5,
                                            borderBottom: index < scannedItems.length - 1 ? '1px solid' : 'none',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Chip
                                            label={index + 1}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ minWidth: 30, height: 20 }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                            {item.qr.length > 40 ? `${item.qr.substring(0, 40)}...` : item.qr}
                                        </Typography>
                                    </Box>
                                ))}
                            </Paper>
                        )}
                    </Box>
                ) : (
                    <>
                        <Paper
                            sx={{
                                width: '100%',
                                aspectRatio: '4/3',
                                bgcolor: 'black',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 3,
                                position: 'relative',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <video
                                ref={videoRef}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />

                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '70%',
                                    aspectRatio: '1/1',
                                    border: '3px solid',
                                    borderColor: isPaused ? 'warning.main' : 'success.main',
                                    borderRadius: 2,
                                    pointerEvents: 'none',
                                    boxShadow: '0 0 0 1000px rgba(0,0,0,0.7)',
                                    animation: (scanning && !isPaused) ? 'pulse 2s infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%': { borderColor: '#00ff00', opacity: 1 },
                                        '50%': { borderColor: '#00cc00', opacity: 0.7 },
                                        '100%': { borderColor: '#00ff00', opacity: 1 },
                                    },
                                    zIndex: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: 20,
                                        height: 20,
                                        borderTop: '3px solid',
                                        borderLeft: '3px solid',
                                        borderColor: 'success.main',
                                        borderTopLeftRadius: 2,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: 20,
                                        height: 20,
                                        borderTop: '3px solid',
                                        borderRight: '3px solid',
                                        borderColor: 'success.main',
                                        borderTopRightRadius: 2,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        width: 20,
                                        height: 20,
                                        borderBottom: '3px solid',
                                        borderLeft: '3px solid',
                                        borderColor: 'success.main',
                                        borderBottomLeftRadius: 2,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        width: 20,
                                        height: 20,
                                        borderBottom: '3px solid',
                                        borderRight: '3px solid',
                                        borderColor: 'success.main',
                                        borderBottomRightRadius: 2,
                                    }}
                                />
                            </Box>

                            {getScannedCount() > 0 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 10,
                                        right: 10,
                                        zIndex: 2,
                                    }}
                                >
                                    <Chip
                                        icon={<CheckCircleIcon />}
                                        label={`${getScannedCount()} scanned`}
                                        color="success"
                                        sx={{ fontWeight: 600 }}
                                    />
                                </Box>
                            )}

                            {isPaused && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        textAlign: 'center',
                                        color: 'white',
                                        zIndex: 2,
                                        backgroundColor: 'rgba(0,0,0,0.8)',
                                        p: 3,
                                        borderRadius: 2,
                                        minWidth: 200,
                                    }}
                                >
                                    <CancelIcon sx={{ fontSize: 48, mb: 2, color: 'warning.main' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Paused
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                        Click "Resume" to continue scanning
                                    </Typography>
                                </Box>
                            )}

                            {!scanning && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        textAlign: 'center',
                                        color: 'white',
                                        zIndex: 2,
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                        p: 3,
                                        borderRadius: 2,
                                    }}
                                >
                                    <CameraIcon sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        Camera starting...
                                    </Typography>
                                    <Typography variant="caption">
                                        Please allow camera permissions
                                    </Typography>
                                </Box>
                            )}
                        </Paper>

                        <canvas ref={canvasRef} style={{ display: 'none' }} />

                        <Alert
                            severity="info"
                            sx={{ mb: 3, borderRadius: 2 }}
                            icon={<QrCodeIcon />}
                        >
                            <Typography variant="body2">
                                <strong>How to scan:</strong> Point the camera at the QR code on the jewelry label.
                                Make sure the entire QR code fits within the green square.
                                {getScannedCount() === 0 && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
                                        💡 Tip: The scanner stays open - scan multiple items back to back!
                                    </Typography>
                                )}
                            </Typography>
                        </Alert>

                        {getScannedCount() > 0 && (
                            <Paper
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    maxHeight: 150,
                                    overflowY: 'auto',
                                    backgroundColor: 'background.default',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                    Scanned Items ({getScannedCount()}):
                                </Typography>
                                <Stack spacing={0.5}>
                                    {scannedItems.slice(-5).reverse().map((item, index) => (
                                        <Box
                                            key={item.timestamp}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                py: 0.5,
                                                borderBottom: index < Math.min(scannedItems.length, 5) - 1 ? '1px solid' : 'none',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Chip
                                                label={scannedItems.length - index}
                                                size="small"
                                                color="success"
                                                sx={{ minWidth: 30, height: 20, fontSize: '0.7rem' }}
                                            />
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                {item.qr.length > 30 ? `${item.qr.substring(0, 30)}...` : item.qr}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {getScannedCount() > 5 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 0.5 }}>
                                            +{getScannedCount() - 5} more items
                                        </Typography>
                                    )}
                                </Stack>
                            </Paper>
                        )}

                        <Paper
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                backgroundColor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <SearchIcon fontSize="small" />
                                Manual Entry
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Can't scan? Enter the QR code manually:
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Enter QR code (e.g., JWL1762407869023)"
                                    value={manualCode}
                                    onChange={(e) => setManualCode(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleManualSubmit();
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <QrCodeIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        }
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={handleManualSubmit}
                                    disabled={!manualCode.trim() || isComplete}
                                    startIcon={<SendIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        minWidth: '100px',
                                        height: '40px',
                                    }}
                                >
                                    Add
                                </Button>
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                The QR code is printed on the jewelry label (e.g., "JWL1762407869023")
                            </Typography>
                        </Paper>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
                {!isComplete ? (
                    <>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                            disabled={isComplete}
                        >
                            Cancel
                        </Button>

                        <Box sx={{ flex: 1 }} />

                        {scanning && (
                            <Button
                                variant="outlined"
                                color={isPaused ? "success" : "warning"}
                                onClick={togglePause}
                                startIcon={isPaused ? <CameraIcon /> : <CancelIcon />}
                                sx={{ borderRadius: 2 }}
                            >
                                {isPaused ? 'Resume' : 'Pause'}
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleComplete}
                            disabled={getScannedCount() === 0}
                            startIcon={<CheckCircleIcon />}
                            sx={{
                                borderRadius: 2,
                                minWidth: '150px',
                            }}
                        >
                            Complete Scanning ({getScannedCount()})
                        </Button>

                        {!scanning && (
                            <Button
                                variant="contained"
                                onClick={startCamera}
                                startIcon={<CameraIcon />}
                                sx={{ borderRadius: 2 }}
                            >
                                Start Camera
                            </Button>
                        )}
                    </>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleCompleteClose}
                        fullWidth
                        sx={{
                            borderRadius: 2,
                            backgroundColor: 'success.main',
                            '&:hover': {
                                backgroundColor: 'success.dark',
                            }
                        }}
                    >
                        Done - Close Scanner
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default QRScanner;